/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState } from 'react';
import {
  Modal, Box, Typography, TextField, Button, Select, MenuItem, FormControl, IconButton
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Pooja } from '../data/pooja-data';
import { stars } from '../data/stars';
import { useCart } from '../context/CartContext';
import CloseIcon from '@mui/icons-material/Close';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';

interface PoojaBookingModalProps {
  open: boolean;
  handleClose: () => void;
  handleCancel?: () => void;
  pooja: Pooja | null;
  selectedPoojas?: Pooja[];
}

const GOLD = '#d4af37';
const GOLD_LIGHT = '#e5c158';

export default function PoojaBookingModal({ open, handleClose, handleCancel, pooja, selectedPoojas = [] }: PoojaBookingModalProps) {
  const { addToCart } = useCart();
  const [name, setName] = useState('');
  const [star, setStar] = useState('ASWATHY');
  const [date, setDate] = useState<Date | null>(new Date());
  const [quantity, setQuantity] = useState(1);
  const [error, setError] = useState('');

  const poojasToBook = selectedPoojas.length > 0 ? selectedPoojas : (pooja ? [pooja] : []);

  const totalPrice = poojasToBook.reduce((sum, p) => sum + p.price, 0) * quantity;

  const handleAddToCart = () => {
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }
    if (poojasToBook.length === 0) return;

    poojasToBook.forEach(p => {
      addToCart({
        id: p.id,
        name: p.name,
        price: p.price,
        quantity,
        bookingName: name,
        bookingStar: star,
        bookingDate: date,
      });
    });

    setName('');
    setStar('ASWATHY');
    setDate(new Date());
    setQuantity(1);
    setError('');
    handleClose();
  };

  const onModalClose = () => {
    setName('');
    setStar('ASWATHY');
    setDate(new Date());
    setQuantity(1);
    setError('');
    if (handleCancel) {
      handleCancel();
    } else {
      handleClose();
    }
  };

  if (poojasToBook.length === 0) return null;

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      bgcolor: '#fafaf8',
      fontSize: '0.9rem',
      transition: 'all 250ms ease',
      '& fieldset': {
        borderColor: 'rgba(0,0,0,0.06)',
        transition: 'all 250ms ease',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(212,175,55,0.3)',
      },
      '&.Mui-focused fieldset': {
        borderColor: GOLD,
        borderWidth: '1.5px',
      },
      '&.Mui-focused': {
        bgcolor: '#fff',
        boxShadow: '0 0 0 4px rgba(212,175,55,0.06)',
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.85rem',
      color: '#bbb',
      '&.Mui-focused': { color: GOLD },
    },
  };

  return (
    <Modal open={open} onClose={onModalClose}>
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '92%', sm: 540, md: 580 },
        maxWidth: 580,
        maxHeight: { xs: '88dvh', sm: '92vh' },
        bgcolor: '#fff',
        borderRadius: '28px',
        boxShadow: '0 32px 80px rgba(0,0,0,0.18), 0 0 0 1px rgba(0,0,0,0.04)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        animation: 'modalSlideIn 400ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        '@keyframes modalSlideIn': {
          '0%': { opacity: 0, transform: 'translate(-50%, -48%) scale(0.95)' },
          '100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
        },
      }}>

        {/* ── Top Header Section ── */}
        <Box sx={{
          position: 'relative',
          background: 'linear-gradient(145deg, #1a1a1a 0%, #2d2520 50%, #1a1a1a 100%)',
          pt: { xs: 3, sm: 3.5 },
          pb: { xs: 5, sm: 6 },
          px: 3,
          overflow: 'hidden',
        }}>
          {/* Decorative pattern */}
          <Box sx={{
            position: 'absolute', inset: 0, opacity: 0.04,
            backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
            backgroundSize: '20px 20px',
          }} />

          {/* Gold shimmer line at top */}
          <Box sx={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 3,
            background: 'linear-gradient(90deg, transparent, ' + GOLD + ', transparent)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 3s infinite linear',
            '@keyframes shimmer': {
              '0%': { backgroundPosition: '-200% 0' },
              '100%': { backgroundPosition: '200% 0' },
            },
          }} />

          {/* Close button */}
          <IconButton
            onClick={onModalClose}
            sx={{
              position: 'absolute', top: 12, right: 12, zIndex: 5,
              width: 36, height: 36,
              bgcolor: 'rgba(255,255,255,0.08)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.1)',
              color: 'rgba(255,255,255,0.6)',
              transition: 'all 250ms ease',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.15)',
                color: '#fff',
                transform: 'rotate(90deg)',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>

          {/* Center icon */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, position: 'relative', zIndex: 2 }}>
            <Box sx={{
              width: 60, height: 60, borderRadius: '50%',
              background: 'linear-gradient(135deg, ' + GOLD + ', ' + GOLD_LIGHT + ')',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 28px rgba(212,175,55,0.35), 0 0 0 6px rgba(212,175,55,0.1)',
              overflow: 'hidden',
            }}>
              <img
                src={process.env.PUBLIC_URL + '/assets/header_god_image.png'}
                alt=""
                style={{ height: '100%', width: '100%', objectFit: 'cover' }}
              />
            </Box>
          </Box>

          {/* Title */}
          <Typography sx={{
            textAlign: 'center', color: '#fff', fontWeight: 800,
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            letterSpacing: '-0.01em', position: 'relative', zIndex: 2,
            mb: 0.5,
          }}>
            {poojasToBook.length > 1
              ? 'Booking ' + poojasToBook.length + ' Poojas'
              : <><span>{(poojasToBook[0]?.name || 'Book Pooja').replace(/\(([^)]+)\)/, '')}</span>
                  {(poojasToBook[0]?.name || '').match(/\(([^)]+)\)/) && (
                    <span className="notranslate" translate="no">{(poojasToBook[0]?.name || '').match(/\(([^)]+)\)/)![0]}</span>
                  )}</>}
          </Typography>

          {/* Subtitle - selected pooja names */}
          {poojasToBook.length > 1 && (
            <Typography sx={{
              textAlign: 'center', color: 'rgba(255,255,255,0.4)',
              fontSize: '0.72rem', position: 'relative', zIndex: 2,
              maxWidth: 360, mx: 'auto',
              display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden',
            }}>
              {poojasToBook.map(p => p.name).join(' \u2022 ')}
            </Typography>
          )}

          {/* Curved bottom */}
          <Box sx={{
            position: 'absolute', bottom: -1, left: 0, right: 0, height: 32,
            bgcolor: '#fff',
            borderRadius: '50% 50% 0 0 / 100% 100% 0 0',
          }} />
        </Box>

        {/* ── Form Section ── */}
        <Box sx={{
          px: { xs: 2.5, sm: 3.5 },
          pt: 0.5,
          pb: 2,
          flex: 1,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: 'rgba(212,175,55,0.2)', borderRadius: 2 },
        }}>

          {/* Name field */}
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PersonOutlineIcon sx={{ fontSize: 16, color: GOLD }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#555', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Devotee Name
              </Typography>
            </Box>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Enter your name"
              value={name}
              onChange={(e) => { setName(e.target.value); setError(''); }}
              error={!!error}
              helperText={error}
              sx={inputSx}
            />
          </Box>

          {/* Star field */}
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <StarOutlineIcon sx={{ fontSize: 16, color: GOLD }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#555', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Birth Star (Nakshatram)
              </Typography>
            </Box>
            <FormControl fullWidth sx={inputSx}>
              <Select
                value={star}
                onChange={(e) => setStar(e.target.value)}
                sx={{
                  borderRadius: '14px !important',
                  bgcolor: '#fafaf8',
                  fontSize: '0.9rem',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(0,0,0,0.06)',
                    transition: 'all 250ms ease',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(212,175,55,0.3)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: GOLD,
                    borderWidth: '1.5px',
                  },
                  '&.Mui-focused': {
                    bgcolor: '#fff',
                    boxShadow: '0 0 0 4px rgba(212,175,55,0.06)',
                  },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: '16px',
                      boxShadow: '0 16px 48px rgba(0,0,0,0.12)',
                      mt: 1,
                      maxHeight: 280,
                      '& .MuiMenuItem-root': {
                        fontSize: '0.85rem',
                        py: 1,
                        '&:hover': { bgcolor: 'rgba(212,175,55,0.06)' },
                        '&.Mui-selected': { bgcolor: 'rgba(212,175,55,0.1)', color: GOLD, fontWeight: 700 },
                      },
                    },
                  },
                }}
              >
                {stars.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          </Box>

          {/* Date field */}
          <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <CalendarTodayIcon sx={{ fontSize: 15, color: GOLD }} />
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#555', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                Pooja Date
              </Typography>
            </Box>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={date}
                onChange={setDate}
                minDate={new Date()}
                slots={{ textField: TextField }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                    sx: inputSx,
                  },
                }}
              />
            </LocalizationProvider>
          </Box>

          {/* Quantity field */}
          <Box sx={{ mb: 1 }}>
            <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#555', letterSpacing: 0.5, textTransform: 'uppercase', mb: 1 }}>
              Quantity
            </Typography>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              bgcolor: '#fafaf8', borderRadius: '14px',
              border: '1px solid rgba(0,0,0,0.06)',
              px: 1.5, py: 0.5,
              width: 'fit-content',
            }}>
              <IconButton
                size="small"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                sx={{
                  width: 34, height: 34, borderRadius: '10px',
                  bgcolor: 'rgba(0,0,0,0.04)',
                  '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' },
                }}
              >
                <RemoveIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Typography sx={{ fontSize: { xs: '0.92rem', md: '1.1rem' }, fontWeight: 800, color: '#1a1a1a', minWidth: 32, textAlign: 'center' }}>
                {quantity}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setQuantity(quantity + 1)}
                sx={{
                  width: 34, height: 34, borderRadius: '10px',
                  bgcolor: 'rgba(0,0,0,0.04)',
                  '&:hover': { bgcolor: 'rgba(212,175,55,0.1)' },
                }}
              >
                <AddIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>
        </Box>

        {/* ── Footer Section ── */}
        <Box sx={{
          px: { xs: 2.5, sm: 3.5 },
          pb: { xs: 2.5, sm: 3 },
          pt: 1,
          borderTop: '1px solid rgba(0,0,0,0.04)',
        }}>
          {/* Total price */}
          <Box sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            mb: 2, px: 2, py: 1.5,
            bgcolor: 'rgba(212,175,55,0.04)',
            borderRadius: '14px',
            border: '1px solid rgba(212,175,55,0.08)',
          }}>
            <Box>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: '#bbb', letterSpacing: 1, textTransform: 'uppercase', lineHeight: 1, mb: 0.3 }}>
                Total Amount
              </Typography>
              <Typography sx={{ fontSize: { xs: '1.05rem', md: '1.3rem' }, fontWeight: 900, color: GOLD, lineHeight: 1 }}>
                {'\u20B9'}{totalPrice.toLocaleString()}
              </Typography>
            </Box>
            <LocalFireDepartmentIcon sx={{ fontSize: 22, color: GOLD, opacity: 0.2 }} />
          </Box>

          {/* Action buttons */}
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              fullWidth
              onClick={onModalClose}
              sx={{
                py: 1.5, borderRadius: '16px',
                fontSize: '0.85rem', fontWeight: 700,
                color: '#999', textTransform: 'none',
                border: '1.5px solid rgba(0,0,0,0.06)',
                transition: 'all 250ms ease',
                '&:hover': {
                  borderColor: 'rgba(0,0,0,0.12)',
                  bgcolor: 'rgba(0,0,0,0.02)',
                  color: '#666',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              onClick={handleAddToCart}
              startIcon={<AddShoppingCartIcon sx={{ fontSize: '18px !important' }} />}
              sx={{
                py: 1.5, borderRadius: '16px',
                fontSize: '0.85rem', fontWeight: 700,
                color: '#fff', textTransform: 'none',
                bgcolor: GOLD,
                boxShadow: '0 6px 24px rgba(212,175,55,0.35)',
                transition: 'all 250ms ease',
                '&:hover': {
                  bgcolor: GOLD_LIGHT,
                  transform: 'translateY(-2px)',
                  boxShadow: '0 10px 32px rgba(212,175,55,0.45)',
                },
              }}
            >
              Add to Cart
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
